import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';

import { AccordionDetails, Box, Collapse, List, ListItem, ListItemIcon, ListItemText, Stack, styled, Typography } from '@mui/material';
import colors from '../../styles/colors';
import messages from '../../utilities/messages';
import Paper from '@mui/material/Paper';
import { getCreatedOpportunities } from '../../utilities/urls';
import LoadingModal from '../../components/Loading';
import SubHeader from '../../components/web/subHeader';
import Header from '../../components/web/header';
import { ProcessTypes } from './JobsProcessesPage';
import ErrorIcon from '@mui/icons-material/Error';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StatusesStack from './components/statusesStack';

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={1} square {...props} />
))(({ theme }) => ({
  border: `1px`,
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(2),
  },
}));

const StatusList = ({ index, status, successfulStatuses, unSuccessfulStatuses, expanded, handleChange }) => {

  return (
    <>
      <Accordion
        expanded={expanded === index}
        sx={{ mt: '10px', border: 1, borderRadius: 4, width: '70%', alignSelf: 'center' }}
        onChange={handleChange}>
        <AccordionSummary>
          <StatusesStack
            status={status}
            unSuccessfulStatuses={unSuccessfulStatuses}
            successfulStatuses={successfulStatuses}
          />
        </AccordionSummary>
        <AccordionDetails>
          <List>
            {status.statuses.length > 0 && status.statuses.map((status, index) => {

              return (
                < ListItem key={index} sx={{ pt: '0px', pb: '0px', backgroundColor: index % 2 === 0 ? '#F6F7F8' : '#fff' }}>
                  <ListItemText
                    primary={status.job}
                    primaryTypographyProps={{
                      fontWeight: '700',
                    }}
                    secondary={status.processName} />
                  <ListItemIcon>
                    {
                      status.status === 'successful'
                        ? <CheckCircleIcon color={'success'} />
                        : <CancelIcon color={'error'} />
                    }

                  </ListItemIcon>
                </ListItem>
              )
            })}
          </List>
        </AccordionDetails>
      </Accordion >
    </>
  )
}

function OpportunitiesList({ isOpportunityCreated, reset, isSubmitted, name, company, setCurrentProcess }) {
  const [allStatuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = React.useState(-1);

  useEffect(() => {
    const getStatuses = async () => {
      try {
        setLoading(true);
        const response = await axios.get(getCreatedOpportunities,
          {
            params: {
              name,
              company
            }
          });

        const statuses = response.data;
        setStatuses(statuses);
        setLoading(false);
      } catch (error) {
        enqueueSnackbar('Error fetching statuses: ' + error, 'error');
        setLoading(false);
      }

    }
    getStatuses();
  }, [isOpportunityCreated]);

  const toastMessage = (
    <Paper sx={{ width: '100%', height: 80, backgroundColor: '#F7FEFE' }} elevation={1}>
      <Box sx={{
        textAlign: 'center'
      }}>
        <Stack sx={{ pt: '20px', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
          <ErrorIcon sx={{ color: colors.primary }} />
          <Typography variant='body1' fontWeight={'600'} ml={'10px'} >Your opportunities are being generated, this may take a while.</Typography>
        </Stack>
        <Typography variant='body2'>When completed, go to UKG and test your opportunities.</Typography>
      </Box>
    </Paper>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', }}>
        <Header
          showBackButton
          title={messages.headerTitles.opportunityCreator}
          onBackClick={() => setCurrentProcess(ProcessTypes.COMPANY)}
        />
        <Collapse
          in={isSubmitted}
          timeout={500}
        >{toastMessage}</Collapse>
        <SubHeader
          title={messages.opportunities}
          subTitle={messages.opportunitySubTitle}
          showRightButton
          handleRightClick={() => {
            isSubmitted && reset();
            setCurrentProcess(ProcessTypes.PUBLISH);
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', }}>
          {
            allStatuses.length > 0 ?
              allStatuses.map((status, index) => {
                const successfulStatuses = status.statuses.length > 0
                  ? status.statuses.filter(processStatus => processStatus.status === 'successful').length : 0;
                const unSuccessfulStatuses = status.statuses.length > 0
                  ? status.statuses.filter(processStatus => processStatus.status === 'unsuccessful').length : 0;

                return (
                  <StatusList
                    index={index}
                    expanded={expanded}
                    key={index}
                    status={status}
                    successfulStatuses={successfulStatuses}
                    unSuccessfulStatuses={unSuccessfulStatuses}
                    handleChange={(event, newExpanded) => setExpanded(newExpanded ? index : -1)} />
                );
              })
              : <>
                <Typography variant="body1" component="div" sx={{ p: '20px', flexGrow: 1, alignSelf: 'center' }}>
                  You have not created any opportunities.
                </Typography>
              </>
          }
          <LoadingModal isLoading={loading} message='Fetching opportunities...' />
        </Box>
      </Box>
    </>
  );
}

export default OpportunitiesList;